/* eslint-disable @typescript-eslint/no-explicit-any */
import * as React from "react";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import { DataGrid, gridClasses } from "@mui/x-data-grid";
import AddIcon from "@mui/icons-material/Add";
import { Button, Alert, Paper } from "@mui/material";
import { useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import PageContainer from "../page-container/page-container";
import { getColumns, pageTitle } from "./data-grid";
import { useNotification } from "../../context/notification-provider";
import useWatchListType from "./use-watch-list";
import { WatchListData } from "./types";
import useWatchListData from "./use-watch-data";
import WatchListDelete from "./delete-dialog";
import LoadingScreen from "../progress/loading-screen";
import CustomToolbar from "../grid-toolbar/custom-toolbar";

const WatchList = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showNotification } = useNotification();

  const [watchListType, setWatchListType] = useState<string>(
    searchParams.get("watchlist") || "",
  );
  const [selectedWatchList, setSelectedWatchList] =
    useState<WatchListData | null>(null);

  const {
    data: watchListTypes,
    isLoading: watchListTypesIsLoading,
    error,
  } = useWatchListType();

  const {
    data: selectedWatchListData,
    error: dataError,
    isLoading: dataIsLoading,
    mutate: dataMutate,
  } = useWatchListData(watchListType);

  // Set default watchListType to first type when watchListTypes loads
  React.useEffect(() => {
    if (
      watchListTypes &&
      watchListTypes.length > 0 &&
      !watchListType &&
      !searchParams.get("watchlist")
    ) {
      const defaultType = watchListTypes[0].id.toString();
      setWatchListType(defaultType);
      setSearchParams({ watchlist: defaultType });
    }
  }, [watchListTypes, watchListType, searchParams, setSearchParams]);

  const handleChange = async (value: string) => {
    setWatchListType(value);
    setSearchParams({ watchlist: value });
  };

  const handleOnRefreshClick = React.useCallback(() => {
    showNotification({
      message: `Refreshing watchlist data...`,
      severity: "info",
      duration: 2000,
    });
    dataMutate();
  }, [showNotification, dataMutate]);

  const handleOnDelete = React.useCallback(
    async (selectedElement: WatchListData) => {
      setSelectedWatchList(selectedElement);
    },
    [],
  );

  const handleAddClick = React.useCallback(() => {
    navigate(`/watch-list/add?watchlist=${watchListType}`);
  }, [navigate, watchListType]);

  const handleOnDeleteClose = () => {
    setSelectedWatchList(null);
    dataMutate();
  };

  if (watchListTypesIsLoading) {
    return <LoadingScreen />;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        Error loading watch list types.
      </Alert>
    );
  }

  return (
    <PageContainer
      title={pageTitle}
      actions={
        <Stack direction="row" alignItems="center" spacing={1}>
          <Button
            variant="contained"
            onClick={handleAddClick}
            startIcon={<AddIcon />}
          >
            Add
          </Button>
        </Stack>
      }
    >
      <Box
        sx={{
          flex: 1,
          width: "100%",
          height: "100%",
        }}
      >
        {dataError ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Error loading watch list data.
          </Alert>
        ) : (
          <Paper
            elevation={2}
            sx={{
              height: "650px",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <DataGrid
              rows={selectedWatchListData || []}
              columns={getColumns({
                onDelete: handleOnDelete,
              })}
              disableRowSelectionOnClick
              loading={dataIsLoading}
              slots={{ toolbar: CustomToolbar }}
              showToolbar
              sx={{
                height: "560px",
                [`& .${gridClasses.columnHeader}, & .${gridClasses.cell}`]: {
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}`]: {
                  backgroundColor: "#f5f6fa",
                  outline: "transparent",
                },
                [`& .${gridClasses.columnHeader}:focus-within, & .${gridClasses.cell}:focus-within`]:
                  {
                    outline: "none",
                  },
                [`& .${gridClasses.row}:hover`]: {
                  cursor: "pointer",
                },
                "& .row-disabled": {
                  opacity: 0.5,
                  pointerEvents: "none",
                },
              }}
              slotProps={{
                toolbar: {
                  filterType: watchListType,
                  filterTypes: watchListTypes?.map((type) => ({
                    type: type.id.toString(),
                    label: type.description,
                  })),
                  onFilterTypeChange: handleChange,
                  onRefreshClick: handleOnRefreshClick,
                } as any,
                loadingOverlay: {
                  variant: "circular-progress",
                  noRowsVariant: "circular-progress",
                },
                baseIconButton: {
                  size: "small",
                },
              }}
            />
          </Paper>
        )}
      </Box>
      {selectedWatchList && (
        <WatchListDelete
          open={Boolean(selectedWatchList)}
          selectedWatchList={selectedWatchList}
          onClose={handleOnDeleteClose}
        />
      )}
    </PageContainer>
  );
};
export default WatchList;
